export const CALCULATOR_FORMULA_NAMED_VALUES = Object.freeze([
    "selectedSourceBiteMinimum",
    "selectedSourceBiteMaximum",
    "sourceAverageBiteTime",
    "fishingLevelFactor",
    "resourceAbundanceFactor",
    "baselineAutoFishingTime",
    "minimumAutoFishingTime",
    "additiveItemAutoFishingReduction",
    "highestPetAutoFishingReduction",
    "appliedAutoFishingReduction",
    "autoFishingReductionCap",
    "activeCatchTime",
    "afkCatchTime",
    "unoptimizedWaitingTime",
    "sessionDuration",
    "sessionHours",
    "secondsPerHour",
    "secondsPerDay",
    "additiveDurabilityReduction",
    "lifeskillDurabilityCurveEntry",
    "brandFactor",
    "baseFishMultiplier",
    "highestFishMultiplierSource",
    "selectedDistanceBonus",
    "distanceBonusUsed",
    "distanceBonusCap",
    "tradePriceCurve",
    "resolvedBargainBonus",
    "baseBargainBonus",
    "bargainBonusPerLevel",
    "tradeLevelIndex",
    "targetAmount",
    "matchingTargetRows",
    "unquantifiedTargetContributions",
    "masteryCurveEntry",
    "resolvedGroupRate",
    "resolvedGroupContributions",
    "allFixedGroupShare",
    "allRawGroupWeight",
    "tradeModifiersDisabled",
    "imperialFishingFixedSaleMultiplier",
    "savedTimeShare",
]);

export const CALCULATOR_FORMULA_PROJECTION_METRICS = Object.freeze([
    "rate",
    "expectedCount",
    "expectedProfit",
    "expectedFishingExperience",
    "expectedLifeExperience",
    "expectedTotemExperience",
    "rawWeight",
]);

export const CALCULATOR_SANKEY_METRICS = Object.freeze([
    "expectedCount",
    "expectedProfit",
    "expectedFishingExperience",
    "expectedTotemExperience",
]);

export const CALCULATOR_SANKEY_ITEM_INPUTS = Object.freeze([
    "uniformInclusiveExpectedQuantity",
    "basePrice",
    "saleMultiplier",
    "baseFishingExperience",
    "fishingExperienceMultiplier",
    "baseTotemExperience",
    "retentionFactor",
]);

const OPERATOR_KEYS = Object.freeze([
    "equals",
    "add",
    "subtract",
    "multiply",
    "divide",
    "openGroup",
    "closeGroup",
    "separator",
]);
const FUNCTION_KEYS = Object.freeze(["minimum", "maximum", "sum", "poissonCdf"]);
const NAMED_VALUE_SET = new Set(CALCULATOR_FORMULA_NAMED_VALUES);
const PROJECTION_METRIC_SET = new Set(CALCULATOR_FORMULA_PROJECTION_METRICS);
const SANKEY_METRIC_SET = new Set(CALCULATOR_SANKEY_METRICS);
const SANKEY_ITEM_INPUT_SET = new Set(CALCULATOR_SANKEY_ITEM_INPUTS);
const NARY_EXPRESSION_KINDS = new Set(["add", "multiply", "minimum", "maximum", "sum"]);
const DEPENDENT_RESULT_KINDS = new Set([
    "named",
    "group",
    "itemGroup",
    "groupMetricShare",
    "itemGroupMetricShare",
]);

export function createCalculatorFormulaPresenter({
    ContractError,
    applyPattern,
    formatNumeric,
    parseNumericValue,
    parseRate,
    requireArray,
    requireExactFields,
    requireFinite,
    requireInteger,
    requireRecord,
    requireString,
}) {
    function parseExactStringTable(value, keys, path) {
        const table = requireExactFields(value, keys, path);
        return Object.fromEntries(keys.map((key) => [
            key,
            requireString(table[key], `${path}.${key}`),
        ]));
    }

    function parseMeta(value) {
        const path = "$.presentation.formulas";
        const formulas = requireExactFields(
            value,
            [
                "functions",
                "groupLabelPattern",
                "groupMetricShareLabelPattern",
                "itemGroupInputLabelPattern",
                "itemGroupLabelPattern",
                "itemGroupMetricShareLabelPattern",
                "label",
                "namedLabels",
                "operators",
                "projectionMetricLabels",
                "sankeyItemInputLabels",
                "unknownItemPattern",
            ],
            path,
        );
        return {
            label: requireString(formulas.label, `${path}.label`),
            namedLabels: parseExactStringTable(
                formulas.namedLabels,
                CALCULATOR_FORMULA_NAMED_VALUES,
                `${path}.namedLabels`,
            ),
            projectionMetricLabels: parseExactStringTable(
                formulas.projectionMetricLabels,
                CALCULATOR_FORMULA_PROJECTION_METRICS,
                `${path}.projectionMetricLabels`,
            ),
            sankeyItemInputLabels: parseExactStringTable(
                formulas.sankeyItemInputLabels,
                CALCULATOR_SANKEY_ITEM_INPUTS,
                `${path}.sankeyItemInputLabels`,
            ),
            groupLabelPattern: requireString(
                formulas.groupLabelPattern,
                `${path}.groupLabelPattern`,
            ),
            groupMetricShareLabelPattern: requireString(
                formulas.groupMetricShareLabelPattern,
                `${path}.groupMetricShareLabelPattern`,
            ),
            itemGroupLabelPattern: requireString(
                formulas.itemGroupLabelPattern,
                `${path}.itemGroupLabelPattern`,
            ),
            itemGroupMetricShareLabelPattern: requireString(
                formulas.itemGroupMetricShareLabelPattern,
                `${path}.itemGroupMetricShareLabelPattern`,
            ),
            itemGroupInputLabelPattern: requireString(
                formulas.itemGroupInputLabelPattern,
                `${path}.itemGroupInputLabelPattern`,
            ),
            unknownItemPattern: requireString(
                formulas.unknownItemPattern,
                `${path}.unknownItemPattern`,
            ),
            operators: parseExactStringTable(
                formulas.operators,
                OPERATOR_KEYS,
                `${path}.operators`,
            ),
            functions: parseExactStringTable(
                formulas.functions,
                FUNCTION_KEYS,
                `${path}.functions`,
            ),
        };
    }

    function parseLabel(value, path) {
        const label = requireRecord(value, path);
        const kind = requireString(label.kind, `${path}.kind`);
        if (kind === "summary") {
            const record = requireExactFields(label, ["kind", "metric"], path);
            return { kind, metric: requireString(record.metric, `${path}.metric`) };
        }
        if (kind === "stat") {
            const record = requireExactFields(label, ["kind", "stat"], path);
            return { kind, stat: requireString(record.stat, `${path}.stat`) };
        }
        if (kind === "statTerm") {
            const record = requireExactFields(label, ["kind", "stat", "termOrder"], path);
            return {
                kind,
                stat: requireString(record.stat, `${path}.stat`),
                termOrder: requireInteger(record.termOrder, `${path}.termOrder`),
            };
        }
        if (kind === "named") {
            const record = requireExactFields(label, ["kind", "name"], path);
            const name = requireString(record.name, `${path}.name`);
            if (!NAMED_VALUE_SET.has(name)) {
                throw new ContractError(`${path}.name`, "unsupported named formula value");
            }
            return { kind, name };
        }
        if (
            kind === "group"
            || kind === "itemGroup"
            || kind === "groupMetricShare"
            || kind === "itemGroupMetricShare"
        ) {
            const isItem = kind === "itemGroup" || kind === "itemGroupMetricShare";
            const fields = !isItem
                ? ["groupId", "kind", "metric"]
                : ["groupId", "itemId", "kind", "metric"];
            const record = requireExactFields(label, fields, path);
            const metric = requireString(record.metric, `${path}.metric`);
            if (!PROJECTION_METRIC_SET.has(metric)) {
                throw new ContractError(`${path}.metric`, "unsupported projection metric");
            }
            return {
                kind,
                groupId: requireString(record.groupId, `${path}.groupId`),
                ...(isItem ? {
                    itemId: requireInteger(record.itemId, `${path}.itemId`, { minimum: 1 }),
                } : {}),
                metric,
            };
        }
        if (kind === "itemGroupInput") {
            const record = requireExactFields(
                label,
                ["groupId", "input", "itemId", "kind"],
                path,
            );
            const input = requireString(record.input, `${path}.input`);
            if (!SANKEY_ITEM_INPUT_SET.has(input)) {
                throw new ContractError(`${path}.input`, "unsupported Sankey item input");
            }
            return {
                kind,
                groupId: requireString(record.groupId, `${path}.groupId`),
                itemId: requireInteger(record.itemId, `${path}.itemId`, { minimum: 1 }),
                input,
            };
        }
        throw new ContractError(`${path}.kind`, "unsupported formula label");
    }

    function parseValue(value, path) {
        const record = requireExactFields(value, ["label", "value"], path);
        return {
            label: parseLabel(record.label, `${path}.label`),
            value: parseNumericValue(record.value, `${path}.value`),
        };
    }

    function parseOperands(value, path, state, depth) {
        const operands = requireArray(value, path);
        if (operands.length === 0) {
            throw new ContractError(path, "expected at least one operand");
        }
        return operands.map((operand, index) => parseExpression(
            operand,
            `${path}[${index}]`,
            state,
            depth + 1,
        ));
    }

    function parseExpression(value, path, state, depth = 0) {
        if (depth > 64) {
            throw new ContractError(path, "formula nesting exceeds 64 levels");
        }
        state.nodes += 1;
        if (state.nodes > 10_000) {
            throw new ContractError(path, "formula exceeds 10000 expression nodes");
        }
        const expression = requireRecord(value, path);
        const kind = requireString(expression.kind, `${path}.kind`);
        if (kind === "value") {
            const record = requireExactFields(expression, ["kind", "value"], path);
            return { kind, value: parseValue(record.value, `${path}.value`) };
        }
        if (kind === "literal") {
            const record = requireExactFields(expression, ["kind", "unit", "value"], path);
            return {
                kind,
                value: requireFinite(record.value, `${path}.value`, { minimum: 0 }),
                unit: requireString(record.unit, `${path}.unit`),
            };
        }
        if (NARY_EXPRESSION_KINDS.has(kind)) {
            const record = requireExactFields(expression, ["kind", "operands"], path);
            return {
                kind,
                operands: parseOperands(record.operands, `${path}.operands`, state, depth),
            };
        }
        if (kind === "subtract" || kind === "divide") {
            const leftKey = kind === "subtract" ? "minuend" : "dividend";
            const rightKey = kind === "subtract" ? "subtrahend" : "divisor";
            const record = requireExactFields(expression, ["kind", leftKey, rightKey], path);
            return {
                kind,
                [leftKey]: parseExpression(
                    record[leftKey],
                    `${path}.${leftKey}`,
                    state,
                    depth + 1,
                ),
                [rightKey]: parseExpression(
                    record[rightKey],
                    `${path}.${rightKey}`,
                    state,
                    depth + 1,
                ),
            };
        }
        if (kind === "poissonCdf") {
            const record = requireExactFields(expression, ["kind", "lambda", "threshold"], path);
            return {
                kind,
                threshold: parseExpression(
                    record.threshold,
                    `${path}.threshold`,
                    state,
                    depth + 1,
                ),
                lambda: parseExpression(
                    record.lambda,
                    `${path}.lambda`,
                    state,
                    depth + 1,
                ),
            };
        }
        throw new ContractError(`${path}.kind`, "unsupported formula expression");
    }

    function parseRows(value, path) {
        const rows = requireArray(value, path);
        if (rows.length === 0) {
            throw new ContractError(path, "expected at least one formula row");
        }
        const state = { nodes: 0 };
        return rows.map((row, index) => {
            const rowPath = `${path}[${index}]`;
            const record = requireExactFields(row, ["expression", "result"], rowPath);
            return {
                result: parseValue(record.result, `${rowPath}.result`),
                expression: parseExpression(record.expression, `${rowPath}.expression`, state),
            };
        });
    }

    function parseSankeyMetricDerivations(value, path) {
        const derivations = new Map();
        requireArray(value, path).forEach((entry, index) => {
            const entryPath = `${path}[${index}]`;
            const record = requireExactFields(
                entry,
                ["formulaRows", "metric", "share"],
                entryPath,
            );
            const metric = requireString(record.metric, `${entryPath}.metric`);
            if (!SANKEY_METRIC_SET.has(metric)) {
                throw new ContractError(`${entryPath}.metric`, "unsupported Sankey metric");
            }
            if (derivations.has(metric)) {
                throw new ContractError(path, `duplicate Sankey metric ${metric}`);
            }
            const share = parseNumericValue(record.share, `${entryPath}.share`);
            if (share.state === "known" && share.unit !== "ratio") {
                throw new ContractError(`${entryPath}.share.unit`, "expected ratio");
            }
            derivations.set(metric, {
                metric,
                share,
                formulaRows: parseRows(record.formulaRows, `${entryPath}.formulaRows`),
                path: entryPath,
            });
        });
        if (
            derivations.size !== CALCULATOR_SANKEY_METRICS.length
            || CALCULATOR_SANKEY_METRICS.some((metric) => !derivations.has(metric))
        ) {
            throw new ContractError(
                path,
                `metrics must be exactly: ${[...CALCULATOR_SANKEY_METRICS].sort().join(", ")}`,
            );
        }
        return derivations;
    }

    function projectionTargets(projection) {
        const groups = new Map();
        const groupMetrics = new Map();
        requireArray(projection.sankey.groups, "$.projection.sankey.groups")
            .forEach((group, index) => {
                const path = `$.projection.sankey.groups[${index}]`;
                const row = requireRecord(group, path);
                const groupId = requireString(row.groupId, `${path}.groupId`);
                if (groups.has(groupId)) {
                    throw new ContractError(path, `duplicate quantified group ${groupId}`);
                }
                groups.set(groupId, { row, path });
                for (const [metric, derivation] of parseSankeyMetricDerivations(
                    row.metrics,
                    `${path}.metrics`,
                )) {
                    groupMetrics.set(`${groupId}\u0000${metric}`, derivation);
                }
            });
        const items = new Map();
        const itemMetrics = new Map();
        requireArray(projection.sankey.items, "$.projection.sankey.items")
            .forEach((item, index) => {
                const path = `$.projection.sankey.items[${index}]`;
                const row = requireRecord(item, path);
                const groupId = requireString(row.groupId, `${path}.groupId`);
                const itemId = requireInteger(row.itemId, `${path}.itemId`, { minimum: 1 });
                const key = `${groupId}\u0000${itemId}`;
                if (items.has(key)) {
                    throw new ContractError(path, `duplicate quantified item ${groupId}/${itemId}`);
                }
                items.set(key, { row, path });
                for (const [metric, derivation] of parseSankeyMetricDerivations(
                    row.metrics,
                    `${path}.metrics`,
                )) {
                    itemMetrics.set(`${key}\u0000${metric}`, derivation);
                }
            });
        return { groups, groupMetrics, items, itemMetrics };
    }

    function projectionMetricValue(target, metric) {
        if (metric === "rate") {
            const rate = parseRate(target.row.rate, `${target.path}.rate`, { quantified: true });
            return { state: "known", unit: "ratio", value: rate.value };
        }
        const units = {
            expectedCount: "count",
            expectedProfit: "silver",
            expectedFishingExperience: "experience",
            expectedLifeExperience: "experience",
            expectedTotemExperience: "experience",
        };
        const value = parseNumericValue(target.row[metric], `${target.path}.${metric}`);
        if (value.state === "known" && value.unit !== units[metric]) {
            throw new ContractError(`${target.path}.${metric}.unit`, `expected ${units[metric]}`);
        }
        return value;
    }

    function validateValueMatches(value, target, path) {
        const matches = value.state === target.state && (
            value.state !== "known"
            || (value.unit === target.unit && value.value === target.value)
        );
        if (!matches) {
            throw new ContractError(path, "resolved value does not match its projection target");
        }
    }

    function resolveValue(value, context, path) {
        const { label } = value;
        let text;
        let target = null;
        if (label.kind === "summary") {
            const summary = context.summaryRows.get(label.metric);
            const meta = context.presentation.summaryMetrics[label.metric];
            if (!summary || !meta) {
                throw new ContractError(`${path}.label.metric`, `dangling summary ${label.metric}`);
            }
            text = meta.title;
            target = summary.value;
        } else if (label.kind === "stat") {
            const stat = context.stats.get(label.stat);
            const meta = context.presentation.stats[label.stat];
            if (!stat || !meta) {
                throw new ContractError(`${path}.label.stat`, `dangling stat ${label.stat}`);
            }
            text = meta.title;
            target = stat.value;
        } else if (label.kind === "statTerm") {
            const stat = context.stats.get(label.stat);
            const term = stat?.termRowsByOrder.get(label.termOrder);
            if (!term) {
                throw new ContractError(
                    `${path}.label.termOrder`,
                    `dangling stat term ${label.stat}/${label.termOrder}`,
                );
            }
            text = term.row.label;
            target = term.value;
        } else if (label.kind === "named") {
            text = context.presentation.formulas.namedLabels[label.name];
        } else if (label.kind === "group" || label.kind === "groupMetricShare") {
            const group = context.presentation.sankeyGroupsByGroupId[label.groupId];
            if (!group) {
                throw new ContractError(`${path}.label.groupId`, `dangling group ${label.groupId}`);
            }
            const pattern = label.kind === "group"
                ? context.presentation.formulas.groupLabelPattern
                : context.presentation.formulas.groupMetricShareLabelPattern;
            text = applyPattern(pattern, {
                group: group.label,
                metric: context.presentation.formulas.projectionMetricLabels[label.metric],
            });
            const projectionTarget = label.kind === "group"
                ? context.projectionTargets.groups.get(label.groupId)
                : context.projectionTargets.groupMetrics.get(
                    `${label.groupId}\u0000${label.metric}`,
                );
            if (label.kind === "groupMetricShare") {
                if (!projectionTarget) {
                    throw new ContractError(
                        `${path}.label.groupId`,
                        `dangling group metric share ${label.groupId}/${label.metric}`,
                    );
                }
                target = projectionTarget.share;
            } else if (projectionTarget && label.metric !== "rawWeight") {
                target = projectionMetricValue(projectionTarget, label.metric);
            }
        } else if (label.kind === "itemGroup" || label.kind === "itemGroupMetricShare") {
            const group = context.presentation.sankeyGroupsByGroupId[label.groupId];
            if (!group) {
                throw new ContractError(`${path}.label.groupId`, `dangling group ${label.groupId}`);
            }
            const identity = context.presentation.sankeyItemsByItemId[String(label.itemId)];
            const itemLabel = identity?.label ?? applyPattern(
                context.presentation.formulas.unknownItemPattern,
                { id: label.itemId },
            );
            const pattern = label.kind === "itemGroup"
                ? context.presentation.formulas.itemGroupLabelPattern
                : context.presentation.formulas.itemGroupMetricShareLabelPattern;
            text = applyPattern(pattern, {
                item: itemLabel,
                group: group.label,
                metric: context.presentation.formulas.projectionMetricLabels[label.metric],
            });
            const itemKey = `${label.groupId}\u0000${label.itemId}`;
            const projectionTarget = label.kind === "itemGroup"
                ? context.projectionTargets.items.get(itemKey)
                : context.projectionTargets.itemMetrics.get(`${itemKey}\u0000${label.metric}`);
            if (!projectionTarget) {
                throw new ContractError(
                    `${path}.label.itemId`,
                    label.kind === "itemGroup"
                        ? `dangling quantified item ${label.groupId}/${label.itemId}`
                        : `dangling item metric share ${label.groupId}/${label.itemId}/${label.metric}`,
                );
            }
            if (label.kind === "itemGroupMetricShare") {
                target = projectionTarget.share;
            } else if (label.metric !== "rawWeight") {
                target = projectionMetricValue(projectionTarget, label.metric);
            }
        } else {
            const group = context.presentation.sankeyGroupsByGroupId[label.groupId];
            if (!group) {
                throw new ContractError(`${path}.label.groupId`, `dangling group ${label.groupId}`);
            }
            const identity = context.presentation.sankeyItemsByItemId[String(label.itemId)];
            const itemLabel = identity?.label ?? applyPattern(
                context.presentation.formulas.unknownItemPattern,
                { id: label.itemId },
            );
            text = applyPattern(context.presentation.formulas.itemGroupInputLabelPattern, {
                item: itemLabel,
                group: group.label,
                input: context.presentation.formulas.sankeyItemInputLabels[label.input],
            });
        }
        if (target) {
            validateValueMatches(value.value, target, `${path}.value`);
        }
        return {
            kind: "term",
            text,
            valueText: formatNumeric(value.value, context.presentation, `${path}.value`).text,
        };
    }

    function operatorToken(text) {
        return { kind: "operator", text };
    }

    function flattenExpression(expression, context, path, nested = false) {
        if (expression.kind === "value") {
            return [resolveValue(expression.value, context, `${path}.value`)];
        }
        if (expression.kind === "literal") {
            const formatted = formatNumeric(
                { state: "known", unit: expression.unit, value: expression.value },
                context.presentation,
                path,
            );
            return [{ kind: "term", text: formatted.text, valueText: "" }];
        }
        const formulas = context.presentation.formulas;
        const wrapArithmetic = (tokens) => nested ? [
            operatorToken(formulas.operators.openGroup),
            ...tokens,
            operatorToken(formulas.operators.closeGroup),
        ] : tokens;
        const join = (operands, operatorKey) => wrapArithmetic(operands.flatMap(
            (operand, index) => [
                ...(index === 0 ? [] : [operatorToken(formulas.operators[operatorKey])]),
                ...flattenExpression(operand, context, `${path}.operands[${index}]`, true),
            ],
        ));
        if (expression.kind === "add" || expression.kind === "multiply") {
            return join(expression.operands, expression.kind);
        }
        if (expression.kind === "subtract" || expression.kind === "divide") {
            const leftKey = expression.kind === "subtract" ? "minuend" : "dividend";
            const rightKey = expression.kind === "subtract" ? "subtrahend" : "divisor";
            return wrapArithmetic([
                ...flattenExpression(expression[leftKey], context, `${path}.${leftKey}`, true),
                operatorToken(formulas.operators[expression.kind]),
                ...flattenExpression(expression[rightKey], context, `${path}.${rightKey}`, true),
            ]);
        }
        const functionArguments = expression.kind === "poissonCdf"
            ? [expression.threshold, expression.lambda]
            : expression.operands;
        return [
            operatorToken(formulas.functions[expression.kind]),
            operatorToken(formulas.operators.openGroup),
            ...functionArguments.flatMap((operand, index) => [
                ...(index === 0 ? [] : [operatorToken(formulas.operators.separator)]),
                ...flattenExpression(
                    operand,
                    context,
                    expression.kind === "poissonCdf"
                        ? `${path}.${index === 0 ? "threshold" : "lambda"}`
                        : `${path}.operands[${index}]`,
                    true,
                ),
            ]),
            operatorToken(formulas.operators.closeGroup),
        ];
    }

    function labelMatchesOwner(label, owner) {
        return label.kind === owner.kind && (
            (owner.kind === "summary" && label.metric === owner.metric)
            || (owner.kind === "stat" && label.stat === owner.stat)
            || (
                owner.kind === "group"
                && label.groupId === owner.groupId
                && label.metric === owner.metric
            )
            || (
                owner.kind === "itemGroup"
                && label.groupId === owner.groupId
                && label.itemId === owner.itemId
                && label.metric === owner.metric
            )
        );
    }

    function presentRows(rows, owner, ownerValue, context, path) {
        const ownerRows = rows.filter((row) => labelMatchesOwner(row.result.label, owner));
        if (ownerRows.length !== 1) {
            throw new ContractError(path, `expected exactly one formula row for owning ${owner.kind}`);
        }
        validateValueMatches(ownerRows[0].result.value, ownerValue, `${path}.owner.result.value`);
        rows.filter((row) => !labelMatchesOwner(row.result.label, owner))
            .forEach((row, index) => {
                if (!DEPENDENT_RESULT_KINDS.has(row.result.label.kind)) {
                    throw new ContractError(
                        `${path}.dependent[${index}].result.label`,
                        "dependent formula result must be named, group, or itemGroup",
                    );
                }
            });
        return rows.map((row, index) => ({
            label: "",
            tokens: [
                resolveValue(row.result, context, `${path}[${index}].result`),
                operatorToken(context.presentation.formulas.operators.equals),
                ...flattenExpression(row.expression, context, `${path}[${index}].expression`),
            ],
        }));
    }

    return Object.freeze({
        parseMeta,
        parseRows,
        parseSankeyMetricDerivations,
        presentRows,
        projectionTargets,
    });
}
